import { useState, useEffect } from "react";
import { Button, Popover } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { IconButton as ToolbarIconButton } from "../IconButton";
import SearchInput from "../../SearchInput";
import SortingIcon from "@/assets/icons/swap.svg?react";
import { MenuFooter } from "../ColumnsMenu/ColumnsMenu";
import { toTitleCase } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import { AvailableList } from "./AvailableList";
import { ActiveList } from "./ActiveList";
import { SortItem, SortingMenuProps } from "./types";

export default function SortingMenu({
  open,
  setOpen,
  hasChanged,
  visibleColumns,
  hiddenColumns,
  sorting,
  setSorting,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  initialSorting,
  columnsTitleCreator = toTitleCase,
  menuHeight,
  menuWidth,
  buttonTestId,
}: SortingMenuProps) {
  const [sortList, setSortList] = useState<SortItem[]>(sorting || []);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setSortList(sorting || []);
  }, [sorting]);

  const handleOpenChange = (newOpen: boolean) => {
    setSearchTerm("");
    if (newOpen) {
      setSortList(sorting || []);
    }
    setOpen(newOpen);
  };

  const handleApply = () => {
    setSorting(sortList);
    setOpen(false);
  };

  const handleReset = () => {
    // const resetValue = initialSorting || [];
    const resetValue: SortItem[] = []; // For now, we are making the Reset All button to remove all sorting selections saved in the workspace.
    setSortList(resetValue);
    setSorting(resetValue);
  };

  return (
    <Popover
      content={
        <div
          data-testid="sorting-menu"
          className="flex flex-col relative"
          style={{ height: menuHeight, width: menuWidth }}
        >
          <div className="flex flex-row flex-1 overflow-hidden">
            {/* Left Panel - Available Columns */}
            <div className="flex flex-col w-1/2 min-w-0 h-full border-r px-2 text-[#2B4752]">
              <div className="mb-3">
                <SearchInput
                  placeholder="Search for data to sort"
                  value={searchTerm}
                  onChange={setSearchTerm}
                />
              </div>
              <div className="flex-1 overflow-y-auto pr-2 hide-scrollbar">
                <AvailableList
                  visibleColumns={visibleColumns}
                  hiddenColumns={hiddenColumns}
                  sortList={sortList}
                  setSortList={setSortList}
                  columnsTitleCreator={columnsTitleCreator}
                  searchTerm={searchTerm}
                />
              </div>
            </div>

            {/* Right Panel - Active Sorts */}
            <div className="flex flex-col w-1/2 min-w-0 h-full pl-4 text-[#2B4752]">
              <div className="flex-1 overflow-y-auto pr-2 hide-scrollbar">
                <ActiveList
                  sortList={sortList}
                  setSortList={setSortList}
                  columnsTitleCreator={columnsTitleCreator}
                />
              </div>
            </div>
          </div>

          <MenuFooter
            onReset={handleReset}
            onApply={handleApply}
            applyDisabled={sortList.length === 0}
          />
        </div>
      }
      title={
        <div className="flex items-center justify-between pb-1 border-b">
          <p className="text-base font-semibold">Sorting</p>
          <Button
            type="text"
            onClick={() => setOpen(false)}
            icon={<CloseOutlined className="text-[#00000073]" />}
          />
        </div>
      }
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      autoAdjustOverflow={false}
      align={{ offset: [0, 10] }}
      destroyOnHidden
    >
      <span>
        <ToolbarIconButton
          icon={<SortingIcon width={24} height={24} />}
          title="Select and sort columns"
          onClick={() => setOpen(!open)}
          isOpen={open}
          isActive={hasChanged}
          mouseEnterDelay={1}
          data-testid={buttonTestId}
        />
      </span>
    </Popover>
  );
}
